'use strict';

import axios from 'axios';
import store from '../store/index';
import { isCognitoEnabled, isNativeCognitoEnabled } from '@/shared/auth/cognitoFeatureFlag';
import { cognitoClient } from '@/shared/auth/cognitoClient';
import { PasswordUpdateError } from './http-error';

// Duplicated declaration of apiBaseUrl (also in commissioningApiClient.ts) to avoid circular dependency.
export const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080';

/**
 * Registers the per-request bearer token resolver and handler for HTTP 401 responses (wired from main.js after Pinia is ready).
 * Handling this way to avoid import cycles.
 **/
let getApiToken = () => null;
let onUnauthorized = async () => {};
export function registerApiBearerTokenResolver(getter) {
  getApiToken = getter;
}
export function registerApiUnauthorizedHandler(handler) {
  onUnauthorized = handler;
}

const solcentreApiClient = axios.create({ baseURL: apiBaseUrl });
const passwordUrls = ['/v2/auth/reset-password', '/v2/auth/change-pwd'];

solcentreApiClient.interceptors.request.use(
  async (config) => {
    if (isNativeCognitoEnabled) {
      if (
        config._nativeSessionVersion !== undefined &&
        config._nativeSessionVersion !== cognitoClient.sessionVersion()
      ) {
        throw new axios.CanceledError('Session changed');
      }
      config._nativeSessionVersion = cognitoClient.sessionVersion();
    }
    const token = await getApiToken();
    if (isNativeCognitoEnabled && config._nativeSessionVersion !== cognitoClient.sessionVersion()) {
      throw new axios.CanceledError('Session changed');
    }
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

solcentreApiClient.interceptors.response.use(
  (response) => {
    if (
      isNativeCognitoEnabled &&
      !passwordUrls.includes(response.config.url) &&
      response.config._nativeSessionVersion !== cognitoClient.sessionVersion()
    ) {
      throw new axios.CanceledError('Session changed');
    }
    return response;
  },
  async (err) => {
    if (
      isNativeCognitoEnabled &&
      (axios.isCancel(err) ||
        (err.config && err.config._nativeSessionVersion !== cognitoClient.sessionVersion()))
    ) {
      return Promise.reject(
        passwordUrls.includes(err.config?.url)
          ? new PasswordUpdateError(err)
          : axios.isCancel(err)
          ? err
          : new axios.CanceledError('Session changed'),
      );
    }
    if (err.config?.url === '/v2/auth/reset-password') {
      return Promise.reject(new PasswordUpdateError(err));
    }
    if (err.response) {
      switch (err.response.status) {
        case 401:
          if (isCognitoEnabled) {
            // TODO: Remove this once Cognito auth is rolled out. For now, don't logout so we have info to debug.
            console.log(
              `(Cognito ENABLED) 401 from main-server. Check UseGuard on ${err.config.url}`,
            );
          } else {
            await onUnauthorized();
          }
          break;
        case 403:
          store.commit('setForbidden', true);
          break;
        default:
          break;
      }
    }
    return Promise.reject(
      passwordUrls.includes(err.config?.url)
        ? new PasswordUpdateError(err)
        : err.response?.data?.message || `request failed with status ${err.response?.status}`,
    );
  },
);

export default solcentreApiClient;
